if (!customElements.get('accessory-bundle')) {
  class AccessoryBundle extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this.handleChange.bind(this));
      this.querySelectorAll('.accessory-bundle__checkbox').forEach((cb) => this.setRowState(cb));

      this.list = this.querySelector('.accessory-bundle__list');
      this.toggle = this.querySelector('.accessory-bundle__toggle');

      if (this.toggle) {
        this.toggle.addEventListener('click', this.toggleCollapsedRows.bind(this));

        // Don't hide a row that is pre-selected, it would be added to the cart unnoticed.
        if (this.querySelector('.accessory-bundle__item--collapsed .accessory-bundle__checkbox:checked')) {
          this.toggleCollapsedRows();
        }
      }
    }

    /**
     * Shows/hides the rows beyond the visible limit.
     */
    toggleCollapsedRows() {
      const expanded = this.list.classList.toggle('is-expanded');
      this.toggle.setAttribute('aria-expanded', expanded);
    }

    /**
     * Handles a change of a row checkbox or variant selector.
     * @param {object} evt - Event object.
     */
    handleChange(evt) {
      if (evt.target.classList.contains('accessory-bundle__select')) {
        AccessoryBundle.updateRowPrice(evt.target);
      } else if (evt.target.classList.contains('accessory-bundle__checkbox')) {
        this.setRowState(evt.target);
      }
    }

    /**
     * Reflects the checked state on the row, for styling purposes.
     * @param {HTMLInputElement} checkbox - Row checkbox.
     */
    setRowState(checkbox) {
      const row = checkbox.closest('.accessory-bundle__item');
      if (row) row.classList.toggle('is-selected', checkbox.checked);
    }

    /**
     * Shows the price of the variant selected in a row.
     * @param {HTMLSelectElement} select - Row variant selector.
     */
    static updateRowPrice(select) {
      const row = select.closest('.accessory-bundle__item');
      const priceEl = row ? row.querySelector('.accessory-bundle__price') : null;
      const option = select.options[select.selectedIndex];

      if (priceEl && option && option.dataset.priceHtml) {
        priceEl.innerHTML = option.dataset.priceHtml;
      }
    }

    /**
     * Returns the items to add to the cart alongside the main product.
     * @returns {Array}
     */
    getSelectedItems() {
      return Array.from(this.querySelectorAll('.accessory-bundle__checkbox:checked'))
        .map((checkbox) => {
          const row = checkbox.closest('.accessory-bundle__item');
          const select = row ? row.querySelector('.accessory-bundle__select') : null;
          return { id: select ? select.value : checkbox.dataset.variantId, quantity: 1 };
        })
        .filter((item) => item.id);
    }

    /**
     * Adds the main product and the selected accessories to the cart in a single request. Each
     * accessory gets a _psbo line item property holding the variant id of the main product, and
     * _psbo_title holding its title for order integrations.
     * @param {HTMLFormElement} form - Main product form.
     * @param {string} mainVariantId - Variant id of the main product.
     * @param {Array} accessories - Accessory items to add.
     */
    async addToCart(form, mainVariantId, accessories) {
      const submitBtn = form.querySelector('[name="add"]');
      const errorMsg = form.querySelector('.js-form-error');
      const cartDrawer = document.querySelector('cart-drawer');
      const qtyInput = form.querySelector('[name="quantity"]');
      const quantity = parseInt(qtyInput ? qtyInput.value : 1, 10) || 1;

      const mainItem = { id: mainVariantId, quantity };
      const properties = {};
      form.querySelectorAll('[name^="properties["]').forEach((input) => {
        if (input.type === 'checkbox' && !input.checked) return;
        const key = input.name.slice(11, -1);
        if (key && input.value) properties[key] = input.value;
      });
      if (Object.keys(properties).length) mainItem.properties = properties;

      const accessoryProperties = { _psbo: String(mainVariantId) };
      if (this.dataset.mainProductTitle) accessoryProperties._psbo_title = this.dataset.mainProductTitle;

      const items = [
        mainItem,
        ...accessories.map((item) => (
          { ...item, properties: { ...accessoryProperties } }
        ))
      ];

      let sections = 'cart-icon-bubble';
      if (cartDrawer) {
        sections += `,${cartDrawer.closest('.shopify-section').id.replace('shopify-section-', '')}`;
      }

      this.dataset.submitting = 'true';
      if (errorMsg) errorMsg.hidden = true;
      if (submitBtn) {
        submitBtn.setAttribute('aria-disabled', 'true');
        submitBtn.classList.add('is-loading');
      }
      if (theme.settings.vibrateOnATC && window.navigator.vibrate) window.navigator.vibrate(100);

      const postItems = async (lineItems) => {
        const response = await fetch(`${theme.routes.cartAdd}.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          body: JSON.stringify({
            items: lineItems,
            sections,
            sections_url: window.location.pathname
          })
        });
        const json = await response.json();

        return { ok: response.ok && !json.status, status: response.status, data: json };
      };

      try {
        let result = await postItems(items);

        // Shopify can't nest add-ons under bundle products or their components
        // ("Bundle products and add-ons can't be mixed"), and refuses the whole
        // request when it happens. Retry as plain lines so the customer still gets
        // what they picked - the _psbo property keeps them grouped in the cart.
        if (!result.ok && items.some((lineItem) => lineItem.parent_id)) {
          result = await postItems(items.map((lineItem) => {
            const flat = { ...lineItem };
            delete flat.parent_id;
            return flat;
          }));
        }

        const { data } = result;

        if (!result.ok) {
          throw new Error(data.description || data.message || result.status);
        }

        const cartIconBubble = document.getElementById('cart-icon-bubble');
        if (cartIconBubble && data.sections && data.sections['cart-icon-bubble']) {
          cartIconBubble.innerHTML = data.sections['cart-icon-bubble'];
        }

        this.dispatchEvent(new CustomEvent('on:cart:add', {
          bubbles: true,
          detail: { variantId: mainVariantId }
        }));

        if (theme.settings.afterAtc === 'page' || theme.settings.afterAtc === 'no-js') {
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
          return;
        }

        if (cartDrawer) {
          if (data.sections) {
            cartDrawer.renderContents(data, theme.settings.afterAtc === 'drawer');
          } else {
            cartDrawer.refresh(true);
          }
        } else if (window.location.pathname === theme.routes.cart) {
          window.location.reload();
        }
      } catch (error) {
        console.log(error); // eslint-disable-line

        if (errorMsg) {
          errorMsg.hidden = false;
          errorMsg.textContent = error.message || theme.strings.cartError;
        }

        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: { error: error.message }
        }));
      } finally {
        this.dataset.submitting = 'false';
        if (submitBtn) {
          submitBtn.classList.add('is-success');
          submitBtn.removeAttribute('aria-disabled');
          setTimeout(() => {
            submitBtn.classList.remove('is-loading');
            submitBtn.classList.remove('is-success');
          }, 1400);
        }
      }
    }
  }

  customElements.define('accessory-bundle', AccessoryBundle);

  /**
   * Takes over the submission of the main product form when accessories are selected, so that the
   * main product and its accessories are added to the cart in one request, in that order.
   * Registered in the capture phase to run before the theme's own product-form handler.
   * @param {object} evt - Submit event object.
   */
  document.addEventListener('submit', (evt) => {
    const form = evt.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('js-product-form-main')) {
      return;
    }

    const section = form.closest('.shopify-section') || document;
    const bundle = section.querySelector('accessory-bundle')
      || document.querySelector('accessory-bundle');
    if (!bundle || bundle.dataset.submitting === 'true') return;

    const submitBtn = form.querySelector('[name="add"]');
    if (submitBtn && submitBtn.getAttribute('aria-disabled') === 'true') return;

    const accessories = bundle.getSelectedItems();
    if (!accessories.length) return;

    const variantInput = form.querySelector('[name="id"]');
    if (!variantInput || !variantInput.value) return;

    evt.preventDefault();
    evt.stopImmediatePropagation();

    bundle.addToCart(form, variantInput.value, accessories);
  }, true);
}
